/**
 * ============================================================================
 * POC — Interactive PDF form fill + auto-marking  (branch: new-functions/pdf-form-fill)
 * ============================================================================
 * Goal: prove that a student can fill an interactive (AcroForm) PDF the normal
 * way — typing into the real form fields — that we can read each blank back
 * BY FIELD NAME, and that those values can be AUTO-MARKED against the answer key
 * that ships EMBEDDED INSIDE THE PDF as an attachment named "answer-key.json".
 *
 * Throwaway, self-contained page (no backend / auth). Load a worksheet PDF via
 * URL or file picker, type into the fields, then click "Save & Mark".
 *
 * Embedded answer-key.json schema (version 2):
 *   {
 *     "version": 2,
 *     "questions": [{ "id": "...", "label": "Q1" }],
 *     "answers": [
 *       { "fieldName": "f1", "questionId": "...", "questionLabel": "Q1",
 *         "partLabel": "a", "answer": "123" }, ...
 *     ]
 *   }
 *
 * How it works (Option A):
 *   1. Display  — react-pdf <Page renderForms> draws real, typeable inputs over
 *                 each page (pdfjs AnnotationLayer). The student types normally.
 *   2. Key      — pdf.getAttachments() pulls "answer-key.json" out of the PDF;
 *                 we map fieldName -> answer.
 *   3. Capture  — typed values live in the pdfjs document annotationStorage; we
 *                 read them by FIELD NAME via getFieldObjects() + the storage map.
 *                 (We deliberately do NOT round-trip through pdf-lib to read
 *                 values — pdfjs saveDocument() writes an incremental update that
 *                 pdf-lib does not reliably resolve, which is why an earlier
 *                 attempt showed blanks even though the saved PDF was correct.)
 *   4. Mark     — compare each captured value to the key => per-field
 *                 correct/incorrect + a score.
 *   5. Save     — pdf.saveDocument() bakes typed values into a real fillable PDF
 *                 (downloaded so it can be archived / opened elsewhere).
 *
 * In production the marking would run server-side (the student app shouldn't be
 * the source of truth for grades), but the key lives in the same place: the
 * embedded answer-key.json.
 * ============================================================================
 */
import React, { useState, useCallback } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"

// react-pdf 7.7.x bundles pdfjs 3.11.174 — pin the worker to the SAME version.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

// Prefilled with the real worksheet PDF so you can load it in one click.
// This is ONLY the textbox default — any PDF URL / local file works.
const DEFAULT_URL =
  "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/activity_pdf/english_activity_pdf/547/28157-interactive%20worksheet%20test.pdf"

const ANSWER_KEY_FILE = "answer-key.json"

// Normalize for comparison: trim + collapse spaces + case-insensitive.
const norm = v => (v ?? "").toString().trim().replace(/\s+/g, " ").toLowerCase()

const PdfFormFillPoc = () => {
  const [fileData, setFileData] = useState(null) // ArrayBuffer | URL string of the chosen PDF
  const [url, setUrl] = useState(DEFAULT_URL)
  const [pdfProxy, setPdfProxy] = useState(null) // pdfjs document proxy (holds annotationStorage)
  const [numPages, setNumPages] = useState(0)
  const [fieldNames, setFieldNames] = useState([]) // detected AcroForm field names
  const [answerKey, setAnswerKey] = useState({}) // { fieldName: answer } from answer-key.json
  const [answerMeta, setAnswerMeta] = useState({}) // { fieldName: { questionLabel, partLabel } }
  const [keyStatus, setKeyStatus] = useState("") // status line for the embedded key
  const [keyFound, setKeyFound] = useState(false)
  const [results, setResults] = useState(null) // [{ name, typed, expected, correct }]
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const resetState = () => {
    setError("")
    setResults(null)
    setPdfProxy(null)
    setNumPages(0)
    setFieldNames([])
    setAnswerKey({})
    setAnswerMeta({})
    setKeyStatus("")
    setKeyFound(false)
  }

  const handleFile = useCallback(e => {
    const file = e.target.files?.[0]
    if (!file) return
    resetState()
    const reader = new FileReader()
    reader.onload = ev => setFileData(ev.target.result)
    reader.readAsArrayBuffer(file)
  }, [])

  // Load straight from a URL (e.g. the S3 worksheet URL from the Network tab).
  // react-pdf fetches it; if S3 blocks the localhost origin you'll see a CORS
  // error here — in that case download the PDF and use the file picker instead.
  const handleLoadUrl = useCallback(() => {
    if (!url) return
    resetState()
    setFileData(url)
  }, [url])

  // Pull the answer key out of the PDF's embedded "answer-key.json" attachment.
  const readEmbeddedAnswerKey = async pdf => {
    const attachments = await pdf.getAttachments() // { filename: { filename, content } } | null
    if (!attachments) return null
    // Match case-insensitively in case authoring tools vary the casing.
    const name = Object.keys(attachments).find(n => n.toLowerCase() === ANSWER_KEY_FILE)
    if (!name) return null
    const text = new TextDecoder("utf-8").decode(attachments[name].content)
    return JSON.parse(text)
  }

  // On load: discover form fields AND auto-load the embedded answer key.
  const onDocumentLoad = useCallback(async pdf => {
    setPdfProxy(pdf)
    setNumPages(pdf.numPages)
    try {
      const fieldObjects = await pdf.getFieldObjects()
      const names = fieldObjects ? Object.keys(fieldObjects) : []
      setFieldNames(names)

      const parsed = await readEmbeddedAnswerKey(pdf)
      if (parsed && Array.isArray(parsed.answers)) {
        const key = {}
        const meta = {}
        for (const a of parsed.answers) {
          key[a.fieldName] = a.answer ?? ""
          meta[a.fieldName] = { questionLabel: a.questionLabel, partLabel: a.partLabel }
        }
        setAnswerKey(key)
        setAnswerMeta(meta)
        setKeyFound(true)
        setKeyStatus(`Loaded ${parsed.answers.length} answer(s) from ${ANSWER_KEY_FILE} (v${parsed.version ?? "?"})`)
      } else {
        setAnswerKey(Object.fromEntries(names.map(n => [n, ""])))
        setKeyFound(false)
        setKeyStatus(`No ${ANSWER_KEY_FILE} embedded in this PDF — cannot auto-mark.`)
      }
    } catch (err) {
      console.error(err)
      setError(err?.message || "Failed to read fields / answer key")
    }
  }, [])

  // Read what the student typed, keyed by field name, from pdfjs annotationStorage.
  const readTypedValues = async () => {
    const fieldObjects = (await pdfProxy.getFieldObjects()) || {}
    const storage = pdfProxy.annotationStorage.getAll() || {} // { annotationId: { value } }
    const out = {}
    for (const [name, annots] of Object.entries(fieldObjects)) {
      let value = ""
      for (const a of annots) {
        const stored = storage[a.id]
        if (stored && stored.value !== undefined && stored.value !== "") {
          value = stored.value
          break
        }
        if (a.value) value = a.value // fall back to a value already baked in the PDF
      }
      out[name] = value
    }
    return out
  }

  const handleSaveAndMark = useCallback(async () => {
    if (!pdfProxy) return
    setBusy(true)
    setError("")
    try {
      // 1) Capture typed values by field name (source of truth: annotationStorage).
      const typed = await readTypedValues()

      // 2) Auto-mark against the embedded answer key.
      const marked = fieldNames.map(name => {
        const expected = answerKey[name] ?? ""
        const typedVal = typed[name] || ""
        const hasKey = name in answerKey && expected !== ""
        const correct = hasKey && norm(typedVal) === norm(expected)
        return { name, typed: typedVal, expected, hasKey, correct, ...answerMeta[name] }
      })
      setResults(marked)

      // 3) Save the filled PDF (values baked in) for archiving / re-opening.
      const filledBytes = await pdfProxy.saveDocument()
      const blob = new Blob([filledBytes], { type: "application/pdf" })
      const dlUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = dlUrl
      a.download = "poc_filled_worksheet.pdf"
      a.click()
      URL.revokeObjectURL(dlUrl)
    } catch (err) {
      console.error(err)
      setError(err?.message || "Failed to save / mark form values")
    } finally {
      setBusy(false)
    }
  }, [pdfProxy, fieldNames, answerKey, answerMeta])

  const scored = results ? results.filter(r => r.hasKey) : []
  const correctCount = scored.filter(r => r.correct).length

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h3>PDF Form-Fill + Auto-Marking POC</h3>
      <p style={{ color: "#555" }}>
        Load an interactive worksheet PDF, type into the form fields, then click{" "}
        <b>Save &amp; Mark</b>. The answer key is read from the PDF&apos;s embedded{" "}
        <code>{ANSWER_KEY_FILE}</code> attachment and answers are graded{" "}
        <b>by field name</b>.
      </p>

      <div style={{ margin: "16px 0", display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="Paste a PDF URL (e.g. the S3 worksheet URL)"
          style={{ flex: 1, padding: "8px 10px" }}
        />
        <button onClick={handleLoadUrl} style={{ padding: "8px 16px", cursor: "pointer" }}>
          Load from URL
        </button>
      </div>

      <div style={{ margin: "16px 0", display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ color: "#777" }}>…or pick a local file:</span>
        <input type="file" accept="application/pdf" onChange={handleFile} />
      </div>

      {error ? <div style={{ color: "crimson", marginBottom: 12 }}>⚠ {error}</div> : null}

      {/* Answer-key panel — appears once a form PDF is loaded. */}
      {fieldNames.length > 0 ? (
        <div style={{ margin: "16px 0", border: "1px solid #ddd", borderRadius: 6, padding: 12 }}>
          <b>Answer key</b>
          <div style={{ color: keyFound ? "green" : "#a00", margin: "6px 0 10px" }}>
            {keyFound ? "✓ " : "⚠ "}
            {keyStatus}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
                <th style={{ padding: 6 }}>Field</th>
                <th style={{ padding: 6 }}>Question</th>
                <th style={{ padding: 6 }}>Correct answer (from {ANSWER_KEY_FILE})</th>
              </tr>
            </thead>
            <tbody>
              {fieldNames.map(name => (
                <tr key={name} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>{name}</td>
                  <td style={{ padding: 6, color: "#777" }}>
                    {answerMeta[name]
                      ? `${answerMeta[name].questionLabel || ""}${
                          answerMeta[name].partLabel ? ` (${answerMeta[name].partLabel})` : ""
                        }`
                      : "—"}
                  </td>
                  <td style={{ padding: 6 }}>
                    {answerKey[name] ? answerKey[name] : <i style={{ color: "#aaa" }}>(no key)</i>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={handleSaveAndMark}
            disabled={!pdfProxy || busy}
            style={{ marginTop: 12, padding: "8px 16px", cursor: pdfProxy ? "pointer" : "not-allowed" }}
          >
            {busy ? "Working…" : "Save & Mark"}
          </button>
        </div>
      ) : null}

      {/* Marking results. */}
      {results ? (
        <div style={{ margin: "16px 0", border: "1px solid #ddd", borderRadius: 6, padding: 12 }}>
          <b>
            Result: {correctCount}/{scored.length} correct
            {scored.length > 0 ? ` (${Math.round((correctCount / scored.length) * 100)}%)` : ""}
          </b>
          <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
                <th style={{ padding: 6 }}>Field</th>
                <th style={{ padding: 6 }}>Typed</th>
                <th style={{ padding: 6 }}>Expected</th>
                <th style={{ padding: 6 }}>Mark</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: 6, fontFamily: "monospace" }}>{r.name}</td>
                  <td style={{ padding: 6 }}>{r.typed || <i style={{ color: "#aaa" }}>(empty)</i>}</td>
                  <td style={{ padding: 6 }}>{r.expected || <i style={{ color: "#aaa" }}>(no key)</i>}</td>
                  <td style={{ padding: 6 }}>
                    {!r.hasKey ? (
                      <span style={{ color: "#aaa" }}>—</span>
                    ) : r.correct ? (
                      <span style={{ color: "green" }}>✓ correct</span>
                    ) : (
                      <span style={{ color: "crimson" }}>✗ wrong</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* No-fields notice. */}
      {pdfProxy && fieldNames.length === 0 ? (
        <div style={{ color: "#a00", margin: "16px 0" }}>
          No named form fields found — this PDF has no fillable AcroForm fields.
        </div>
      ) : null}

      {fileData ? (
        <div style={{ border: "1px solid #ccc", borderRadius: 6, background: "#f7f7f7" }}>
          <Document
            file={fileData}
            onLoadSuccess={onDocumentLoad}
            onLoadError={err => setError(err?.message || "Failed to load PDF")}
            loading="Loading PDF…"
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "center", padding: 12 }}>
                <Page
                  pageNumber={i + 1}
                  renderForms /* <-- the key flag: interactive, typeable fields */
                  renderAnnotationLayer
                  renderTextLayer={false}
                  scale={1.3}
                />
              </div>
            ))}
          </Document>
        </div>
      ) : null}
    </div>
  )
}

export default PdfFormFillPoc
